/**
 * MaleHealthTwinPanel — iter752 (Feb 2026)
 *
 * Gender-specific companion panel to the 18 unisex Digital Twins. Renders
 * only when the current user has `gender === "male"`. Shows 3 clinical
 * axes with real KEGG pathway references and Truthful Patterns (no fake
 * baseline scores — insufficient_data → "Awaiting data" band).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import {
  Mars, Activity, FlaskConical, Scale, ChevronRight, Loader2, Info,
} from "lucide-react";

const AXIS_META = {
  androgen_estrogen: {
    icon: FlaskConical,
    color: "#6366f1",
    accent: "indigo",
  },
  prostate_metabolic: {
    icon: Activity,
    color: "#f97316",
    accent: "orange",
  },
  adipose_aromatase: {
    icon: Scale,
    color: "#a855f7",
    accent: "violet",
  },
};

const BAND_STYLES = {
  optimal:          { label: "Оптимална",   color: "#10b981" },
  good:             { label: "Добра",       color: "#22c55e" },
  borderline:       { label: "Гранична",    color: "#f59e0b" },
  concern:          { label: "Внимание",    color: "#f97316" },
  high_burden:      { label: "Висок товар", color: "#ef4444" },
  insufficient_data:{ label: "Данни в очакване", color: "#94a3b8" },
};

export default function MaleHealthTwinPanel({ compact = false }) {
  const { API, user } = useAuth();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const rawGender = user?.gender || user?.profile?.personal?.gender || user?.profile?.gender;
  const isMale = String(rawGender || "").toLowerCase() === "male";

  useEffect(() => {
    if (!isMale) { setLoading(false); return; }
    let cancelled = false;
    axios
      .get(`${API}/male-health-twin/snapshot`, { timeout: 15000 })
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [API, isMale]);

  if (!isMale) return null; // gender-gated

  if (loading) {
    return (
      <div
        data-testid="male-health-twin-loading"
        className={`p-5 rounded-2xl border ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"} flex items-center justify-center min-h-[120px]`}
      >
        <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!data || data.state === "error") return null;
  if (data.state === "not_applicable") return null;

  const axes = data.axes || {};
  const composite = data.composite_score;
  const band = data.composite_band || "insufficient_data";
  const bandStyle = BAND_STYLES[band] || BAND_STYLES.insufficient_data;

  return (
    <section
      data-testid="male-health-twin-panel"
      className={`p-5 rounded-2xl border ${
        isDark
          ? "bg-gradient-to-br from-slate-900/80 via-indigo-950/50 to-slate-900/80 border-indigo-800/40"
          : "bg-gradient-to-br from-indigo-50 via-white to-violet-50 border-indigo-200"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isDark ? "bg-indigo-900/60" : "bg-indigo-100"
            }`}
          >
            <Mars className="w-5 h-5 text-indigo-500" />
          </div>
          <div>
            <div className={`text-[10px] uppercase font-bold tracking-widest ${isDark ? "text-indigo-300" : "text-indigo-600"}`}>
              Мъжки Health Twin · iter752
            </div>
            <h3 className={`text-lg font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
              Андроген · Простата · Ароматаза
            </h3>
          </div>
        </div>
        {composite !== null && composite !== undefined && (
          <div className="text-right">
            <div
              data-testid="male-health-twin-composite"
              className="text-3xl font-bold"
              style={{ color: bandStyle.color }}
            >
              {composite}
            </div>
            <div className={`text-[10px] uppercase font-semibold`} style={{ color: bandStyle.color }}>
              {bandStyle.label}
            </div>
          </div>
        )}
      </div>

      {/* Axes grid */}
      <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-3"} gap-3`}>
        {Object.entries(axes).map(([key, ax]) => {
          const meta = AXIS_META[key] || { icon: Info, color: "#64748b" };
          const Icon = meta.icon;
          const axBand = BAND_STYLES[ax.band] || BAND_STYLES.insufficient_data;
          const isOpen = expanded === key;
          return (
            <div
              key={key}
              data-testid={`male-twin-axis-${key}`}
              className={`rounded-xl border transition-all ${
                isDark ? "bg-slate-950/60 border-slate-800" : "bg-white border-slate-200"
              } ${isOpen ? "shadow-lg" : ""}`}
            >
              <button
                onClick={() => setExpanded(isOpen ? null : key)}
                className="w-full text-left p-4 flex items-start gap-3"
                data-testid={`male-twin-axis-${key}-toggle`}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${meta.color}22`, color: meta.color }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className={`text-[13px] font-semibold ${isDark ? "text-slate-100" : "text-slate-900"} truncate`}>
                      {ax.label_bg}
                    </div>
                    <div className="text-lg font-bold flex-shrink-0" style={{ color: axBand.color }}>
                      {ax.score !== null && ax.score !== undefined ? ax.score : "—"}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] uppercase font-bold" style={{ color: axBand.color }}>
                      {axBand.label}
                    </span>
                    <ChevronRight
                      className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""} ${
                        isDark ? "text-slate-500" : "text-slate-400"
                      }`}
                    />
                  </div>
                  {/* Score bar */}
                  <div className={`h-1.5 rounded-full mt-2 overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
                    {ax.score !== null && ax.score !== undefined && (
                      <div
                        className="h-full transition-all"
                        style={{ width: `${ax.score}%`, background: axBand.color }}
                      />
                    )}
                  </div>
                </div>
              </button>

              {isOpen && (
                <div className={`px-4 pb-4 pt-1 border-t ${isDark ? "border-slate-800" : "border-slate-200"}`} data-testid={`male-twin-axis-${key}-detail`}>
                  {ax.measured_signals?.length > 0 && (
                    <div className="mt-2">
                      <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
                        Използвани сигнали
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {ax.measured_signals.map((s, i) => (
                          <span
                            key={i}
                            className={`text-[11px] px-2 py-0.5 rounded-full ${
                              isDark ? "bg-emerald-900/40 text-emerald-200" : "bg-emerald-50 text-emerald-800"
                            }`}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {ax.missing_signals?.length > 0 && (
                    <div className="mt-3">
                      <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                        Липсват
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {ax.missing_signals.map((s, i) => (
                          <span
                            key={i}
                            className={`text-[11px] px-2 py-0.5 rounded-full ${
                              isDark ? "bg-amber-900/30 text-amber-200" : "bg-amber-50 text-amber-800"
                            }`}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {ax.factors?.length > 0 && (
                    <div className="mt-3">
                      <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                        Открити фактори
                      </div>
                      <ul className={`text-[12px] leading-relaxed list-disc pl-4 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                        {ax.factors.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {ax.ratio_t_e2 !== undefined && ax.ratio_t_e2 !== null && (
                    <div className={`mt-3 p-2 rounded-md ${isDark ? "bg-indigo-900/30" : "bg-indigo-50"}`}>
                      <span className={`text-[11px] font-bold ${isDark ? "text-indigo-200" : "text-indigo-800"}`}>
                        T:E2 ratio = {ax.ratio_t_e2}
                      </span>
                    </div>
                  )}
                  {ax.kegg_pathways?.length > 0 && (
                    <div className="mt-3">
                      <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                        KEGG pathways
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {ax.kegg_pathways.map((p, i) => (
                          <code
                            key={i}
                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                              isDark ? "bg-violet-900/40 text-violet-200" : "bg-violet-100 text-violet-800"
                            }`}
                          >
                            {p}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                  {ax.notes_bg && (
                    <p className={`text-[11px] leading-relaxed mt-3 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                      {ax.notes_bg}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer — coverage + disclaimer */}
      <div className={`mt-4 pt-3 border-t flex items-start gap-2 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
        <Info className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
        <div className="flex-1">
          <div className={`text-[11px] font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            {data.measured_axes ?? 0}/{data.total_axes ?? 3} активни оси
          </div>
          <p className={`text-[10px] leading-relaxed ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {data.disclaimer_bg ||
              "Образователен инструмент, не е медицинско устройство. Не заменя консултация с ендокринолог или уролог."}
          </p>
        </div>
      </div>
    </section>
  );
}
